const warned = new Set<string>()

type DevWarnMissingAccessibleNameOptions = {
  componentName: string
  /**
   * HTML role
   * - `presentation` / `none` => considered decorative
   */
  role?: React.AriaRole
  /**
   * If `true`, the element is hidden from assistive technologies.
   */
  ariaHidden?: React.AriaAttributes['aria-hidden']
  /**
   * Accessible name
   */
  ariaLabel?: React.AriaAttributes['aria-label']
  ariaLabelledBy?: React.AriaAttributes['aria-labelledby']
}

/**
 * Warns in development if a component is missing an accessible name while not being explicitly hidden from screen readers.
 *
 * This utility helps ensure that components providing information or functionality are accessible.
 * It checks if the component has an `aria-label`, `aria-labelledby`, is `aria-hidden`, or has a presentational role.
 *
 * @example
 * // 1. Component that provides information (Warning if missing label)
 * devWarnMissingAccessibleNameOnce({
 *   componentName: 'StatusDot',
 *   ariaLabel: props['aria-label'],
 *   ariaHidden: props['aria-hidden'],
 * })
 *
 * @example
 * // 2. Purely decorative component (No warning because ariaHidden is true)
 * devWarnMissingAccessibleNameOnce({
 *   componentName: 'StatusDot',
 *   ariaHidden: true,
 * })
 *
 * @example
 * // 3. Presentational component (No warning because of role)
 * devWarnMissingAccessibleNameOnce({
 *   componentName: 'Icon',
 *   role: 'presentation',
 * })
 */
export function devWarnMissingAccessibleNameOnce({
  componentName,
  role,
  ariaHidden,
  ariaLabel,
  ariaLabelledBy,
}: DevWarnMissingAccessibleNameOptions) {
  if (process.env.NODE_ENV !== 'development') return

  const isAriaHidden = ariaHidden === true || ariaHidden === 'true'
  const isPresentational = role === 'presentation' || role === 'none'
  const hasAccessibleName = Boolean(ariaLabel || ariaLabelledBy)

  if (isAriaHidden || isPresentational || hasAccessibleName) return

  const key = `${componentName}:missing-accessible-name`
  if (warned.has(key)) return
  warned.add(key)

  console.warn(
    [
      `[${componentName}] Missing accessible name.`,
      'If this element conveys information, provide `aria-label` or `aria-labelledby`.',
      'If it is purely decorative, set `aria-hidden` (or `role="presentation"`).',
    ].join(' '),
  )
}
